import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Divider } from '@mui/material';
import InitialSortTypeEnum from '@/utils/constants/InitialSortTypeEnum';
import { setOrdInQuerySearchParams } from '@/utils/functions/setOrdInQuerySearchParams';
import { checkInitialParams } from '@/utils/functions/checkInitialParams';

import styles from '@/views/components/Footer/Footer.module.scss';

import smartcity_logo from '@/assets/images/logo_smartcity.svg';
import { CheckPermission } from '@/utils/permissions/permissions';

const Footer = ({ narrow, noLoggedInLinks = false, className = '' }) => {
	const { MODIFIED_ON: { value: ORD_MODIFIED_ON }, INPUT_PHRASE: { value: ORD_INPUT_PHRASE }, NAME: { value: ORD_NAME }, SURNAME: { value: ORD_SURNAME } } = InitialSortTypeEnum;

	const { dict, ppj } = useSelector((state) => state.app);
	const user = useSelector((state) => state.auth.user);

	const {title} = dict.footer;
	const {simpletext, letters, templates, dictionary, myAccount, listUsers, listDeps, changePassword, serviceManagement, ppjLabel, findAdvice, myQuestions, askQuestion, manageQuestions} = dict.footer.links;

	const links1 = [
		{
			name: letters,
			path: `/letters?${checkInitialParams(ORD_MODIFIED_ON, location.search, user)}`,
		},
		{
			name: templates,
			path: `/templates?${checkInitialParams(ORD_MODIFIED_ON, location.search, user)}`,
		},
		{
			name: simpletext,
			path: "/simple-text",
		},
		{
			name: dictionary,
			path: `/dict${setOrdInQuerySearchParams(ORD_INPUT_PHRASE)}`,
		},
	];

	const userRole = user && user.role;
	const isExternalAccountUser = user && user.provider !== null;

	const generateLink = (link, small) => (<Link key={ link.path }
		to={ link.path }
		className={ small ? `${ styles['footer__list__item'] } ${ styles["footer__list__item--small"] }` : styles['footer__list__item'] }
		onClick={ () => { window.scrollTo(0, 0); } }
	>
		{link.name}
	</Link>);

	return (
		<>
			{narrow ? (
				<footer className={ `${ styles.footer } noselect ${ styles['footer--narrow'] } ${className}` }>
					<div>
						{dict.letter.footer.t1}&nbsp;
						<a className={ styles['footer--link'] }
							target="_blank"
							href="https://www.pcss.pl/"
							rel="noopener noreferrer">{dict.letter.footer.t2}</a>&nbsp;
						{dict.letter.footer.t3}&nbsp;
						<a className={ styles['footer--link'] }
							target="_blank"
							href="https://www.poznan.pl/"
							rel="noopener noreferrer">{dict.letter.footer.t4}</a>&nbsp;
						{dict.letter.footer.t5}&nbsp;
						<a className={ styles['footer--link'] }
							target="_blank"
							href="https://amu.edu.pl/"
							rel="noopener noreferrer">{dict.letter.footer.t6}</a>&nbsp;
						{dict.letter.footer.t7}&nbsp;
						<Link
							className={ styles['footer--link'] }
							to="/cookies"
						>{dict.letter.footer.t8}</Link>
					</div>
				</footer>
			) : (
				<footer className={ `${ styles['footer'] } ${ styles['footer--map'] } ${className}` }>
					{ !noLoggedInLinks ? <div className={ styles['footer__list'] }>
						<span className={ styles['footer__list__title'] }>{title}</span>
						<div className={ styles['footer__list__container'] }>
							<div className={ styles['footer__list__items'] }>
								{links1.map((link) => (generateLink(link)))}
							</div>

							<Divider orientation="vertical"
								flexItem
								sx={ {borderColor: "#5C5C5A", height: "147px", alignSelf: 'center'} }
							/>

							{ppj?.enabled && (
								<>
									<div className={ styles['footer__list__items'] }>
										<span
											className={ styles['footer__list__title'] }
										>
											{ppjLabel}
										</span>
										{generateLink({name: findAdvice, path: `/simple-text-advices`})}
										{CheckPermission("canAskQuestions", userRole) && generateLink({name: myQuestions, path: `/simple-text-my-advices`})}
										{CheckPermission("canAskQuestions", userRole) && generateLink({name: askQuestion, path: `/simple-text-my-advices?question`})}
										{CheckPermission("canManageQuestions", userRole) && generateLink({name: manageQuestions, path: `/simple-text-advices-admin`})}
									</div>
									<Divider orientation="vertical"
										flexItem
										sx={ {borderColor: "#5C5C5A", height: "147px", alignSelf: 'center'} }
									/>
								</>
							)}

							<div className={ styles['footer__list__items'] }>
								<span
									className={ styles['footer__list__title'] }
								>
									{myAccount}
								</span>
								<div className={ styles['footer__list__container--inlist'] }>
									{CheckPermission("canOpenUserManagement", userRole) || CheckPermission("canOpenDepartmentManagement", userRole) && <div className='footer__list__items' >
										<span className = { styles['footer__list__title--small'] }>{serviceManagement}</span>
										{CheckPermission("canOpenUserManagement", userRole) && generateLink({name: listUsers, path: `/users${setOrdInQuerySearchParams(ORD_SURNAME)}`}, false)}
										{CheckPermission("canOpenDepartmentManagement", userRole) && generateLink({name: listDeps, path: `/departments/${setOrdInQuerySearchParams(ORD_NAME)}`}, false)}
									</div>}
									{!isExternalAccountUser && <div className = { styles['footer__list__items'] }>
										<span
											className = { styles['footer__list__title--small'] }
										>
											{myAccount}
										</span>
										{generateLink({name: changePassword, path: "/change-password"}, false)}
									</div>}
								</div>
							</div>
						</div>
					</div> : <div></div> }
					<div className={ styles["footer__img--container"] }>
						<img src={ smartcity_logo }
							className={ styles["footer__img"] }
							alt={ 'Logo smartcity' } />
					</div>
					<div className={ `${ styles["footer__text"] } ${ styles["footer__text--map"] }` }>
						{dict.letter.footer.t1}
						<a
							target="_blank"
							href="https://www.pcss.pl/"
							rel="noopener noreferrer">{dict.letter.footer.t2}</a>
						{dict.letter.footer.t9}&nbsp;{dict.letter.footer.t3}
						<a
							target="_blank"
							href="https://www.poznan.pl/"
							rel="noopener noreferrer"> {dict.letter.footer.t4} </a>
						{dict.letter.footer.t5}
						<a
							target="_blank"
							href="https://amu.edu.pl/"
							rel="noopener noreferrer"> {dict.letter.footer.t6} </a> <br/>
						{dict.letter.footer.t7}
						<Link
							to="/cookies"
						> {dict.letter.footer.t8} </Link>
					</div>
				</footer>
			)}
		</>
	);
};
export default Footer;
